import { useMemo, useState } from "react";
import { createTagDef } from "../actions";
import { useTagDefs, type TagDef, type TagKind } from "../data";

/**
 * A tag's colour: one of eight pastels, from a hash of its id.
 *
 * From the id and never from its position, so "תושב" is the same green on the
 * person screen, in the Inbox and in a list — a colour that moved when the list
 * reordered would be noise, not information.
 */
export function tagColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return `tag-c${Math.abs(h) % 8}`;
}

/** A tag as a coloured chip, for showing rather than choosing. */
export function TagChip({ id, name, onRemove, busy }: {
  id: string;
  name: string;
  onRemove?: () => void;
  busy?: boolean;
}) {
  return (
    <span className={`tagc ${tagColor(id)}`}>
      {name}
      {onRemove && (
        <button className="tagc-x" aria-label={`הסרת ${name}`} onClick={onRemove} disabled={busy}>
          ×
        </button>
      )}
    </span>
  );
}

/**
 * Choosing tags, the same way everywhere.
 *
 * Asked for 2026-09-24: "המסך של התגיות לא ידידותי, אפשר שיהיו בצבעי פסטל שונים.
 * וגם זה יותר מידי תגיות, צריך להראות רק את מה שבשימוש הכי הרבה ואת השאר תחת
 * 'עוד'. וגם אני צריך אפשרות קיצור דרך להוסיף תגיות חדשות גם במסך הזה".
 *
 *   - **Colour** per tag, from `tagColor`.
 *   - **The most used first** (`usageCount`, refreshed nightly), `limit` of them;
 *     the rest behind "עוד", which also opens a search over all of them. What is
 *     already chosen always shows, so a selection never scrolls out of sight.
 *   - **A new tag from here**: "+ תגית חדשה", typed and saved on the spot, and
 *     chosen straight away. No trip to Settings.
 */
export function TagPicker({
  kind = "person",
  selected,
  onToggle,
  busy,
  limit = 8,
}: {
  kind?: TagKind;
  selected: string[];
  onToggle: (tagId: string, on: boolean) => void;
  busy?: boolean;
  limit?: number;
}) {
  const { data } = useTagDefs(kind);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const active = useMemo(() => data.filter((t) => t.status !== "hidden"), [data]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matching = q === "" ? active : active.filter((t) => (t.name ?? "").toLowerCase().includes(q));
    const byUse = (a: TagDef, b: TagDef) =>
      (b.usageCount ?? 0) - (a.usageCount ?? 0) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      (a.name ?? "").localeCompare(b.name ?? "", "he");
    const chosen = matching.filter((t) => selected.includes(t.id)).sort(byUse);
    const rest = matching.filter((t) => !selected.includes(t.id)).sort(byUse);
    if (expanded || q !== "") return [...chosen, ...rest];
    return [...chosen, ...rest.slice(0, Math.max(0, limit - chosen.length))];
  }, [active, selected, filter, expanded, limit]);

  const hiddenCount = active.length - shown.length;

  async function create() {
    const name = newName.trim();
    if (name.length < 2) {
      setErr("כתבו לפחות 2 אותיות.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const id = await createTagDef(name, kind);
      onToggle(id, true);
      setNewName("");
      setCreating(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו ליצור את התגית. נסו שוב.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tagpick">
      {(expanded || filter !== "") && (
        <input
          className="tag-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="חיפוש תגית…"
          autoComplete="off"
          disabled={busy}
        />
      )}

      <div className="chips">
        {shown.map((t) => {
          const on = selected.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              className={`tagc ${tagColor(t.id)}${on ? " on" : ""}`}
              aria-pressed={on}
              onClick={() => onToggle(t.id, !on)}
              disabled={busy}
            >
              {on && "✓ "}
              {t.name}
            </button>
          );
        })}
        {!expanded && hiddenCount > 0 && (
          <button type="button" className="tagc tagc-quiet" onClick={() => setExpanded(true)} disabled={busy}>
            עוד {hiddenCount}…
          </button>
        )}
        {!creating && (
          <button type="button" className="tagc tagc-new" onClick={() => setCreating(true)} disabled={busy}>
            + תגית חדשה
          </button>
        )}
      </div>

      {creating && (
        <div className="tagpick-new">
          <input
            className="form-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
            }}
            placeholder="שם התגית החדשה"
            autoFocus
            disabled={saving}
            maxLength={40}
          />
          <button className="btn btn-primary btn-sm" onClick={() => void create()} disabled={saving}>
            {saving ? "יוצרים…" : "הוספה"}
          </button>
          <button
            className="btn btn-sm"
            onClick={() => {
              setCreating(false);
              setErr(null);
            }}
            disabled={saving}
          >
            ביטול
          </button>
        </div>
      )}
      {err && <p className="form-error">{err}</p>}
    </div>
  );
}
